import * as network from '../network'
import * as outbox from './outbox'
import * as photos from './photos'

/**
 * Обработчик очереди.
 *
 * Запускается при появлении сети, по таймеру и вручную. Background
 * Sync используется где поддерживается — он позволяет доставить
 * задачи, даже когда приложение закрыто, но есть только в Chromium
 * и требует service worker, поэтому это дополнение, а не основа.
 */

const TICK_MS = 15_000

let timer = null
let running = false
let handlers = {}
let listeners = []
let unsubscribeNetwork = null

/** Ошибки, после которых повторять бессмысленно. */
function isPermanent(error) {
  const status = error?.response?.status
  if (!status) return false
  // 4xx — данные не подойдут и в следующий раз. Кроме 408 и 429:
  // это «попробуйте позже», а не «так нельзя».
  return status >= 400 && status < 500 && status !== 408 && status !== 429
}

function notify() {
  listeners.forEach((fn) => {
    try {
      fn()
    } catch {
      /* подписчик не должен ронять обработчик */
    }
  })
}

export function subscribe(fn) {
  listeners.push(fn)
  return () => {
    listeners = listeners.filter((l) => l !== fn)
  }
}

/**
 * Обработать очередь.
 *
 * Задачи идут последовательно, а не параллельно: на мобильной сети
 * шесть одновременных загрузок делят и без того узкий канал,
 * и все шесть отваливаются по таймауту вместо того, чтобы одна
 * прошла целиком.
 */
export async function flush() {
  if (running || !network.isOnline()) return { sent: 0, failed: 0 }

  running = true
  let sent = 0
  let failed = 0

  try {
    const tasks = await outbox.due()

    for (const task of tasks) {
      const handler = handlers[task.type]
      if (!handler) {
        await outbox.markFailed(task.id, `Нет обработчика для ${task.type}`, {
          permanent: true,
        })
        failed += 1
        continue
      }

      await outbox.markInFlight(task.id)
      notify()

      try {
        await handler(task)
        await outbox.markDone(task.id)
        sent += 1
      } catch (error) {
        await outbox.markFailed(task.id, error?.message || error, {
          permanent: isPermanent(error),
        })
        failed += 1

        // Сеть пропала посреди разгрузки — остальные задачи
        // всё равно не уйдут, а попытки съедят батарею.
        if (!network.isOnline()) break
      }

      notify()
    }
  } finally {
    running = false
    notify()
  }

  return { sent, failed }
}

/**
 * Запустить обработчик.
 *
 * @param {Record<string, (task) => Promise<void>>} taskHandlers
 */
export async function start(taskHandlers) {
  handlers = taskHandlers

  // Задачи, оставшиеся in_flight после закрытия приложения,
  // сами по себе не повторятся никогда.
  await outbox.recoverStuck()
  await photos.enforceQuota()

  clearInterval(timer)
  timer = setInterval(flush, TICK_MS)

  unsubscribeNetwork?.()
  unsubscribeNetwork = network.subscribe((online) => {
    if (online) flush()
  })

  await registerBackgroundSync()

  // Дожидаемся первой разгрузки: без этого вызывающий не знает,
  // когда очередь обработана, и индикатор показывает устаревшее
  // число сразу после запуска.
  await flush()
}

export function stop() {
  clearInterval(timer)
  timer = null
  unsubscribeNetwork?.()
  unsubscribeNetwork = null
  handlers = {}
  listeners = []
}

/**
 * Background Sync.
 *
 * Позволяет браузеру доставить задачи, когда приложение уже закрыто.
 * Есть не везде — поэтому таймер и слушатель сети остаются основным
 * механизмом, а это дополнение к ним.
 */
async function registerBackgroundSync() {
  try {
    const reg = await globalThis.navigator?.serviceWorker?.ready
    if (!reg?.sync) return false
    await reg.sync.register('carrent-outbox')
    return true
  } catch {
    return false
  }
}

export const isSupported = () => Boolean(globalThis.navigator?.serviceWorker)
